import React, { useState, useEffect } from "react";
import axios from "axios";
import ImageSection from "./ImageSection";

const cleanStoryText = (text = "") =>
  text
    .replace(/^\s*(\d+[\.\)]\s+)?/, "")
    .replace(/\*/g, "")
    .trim();

const topicIdeas = [
  { label: "Water Cycle", value: "how the water cycle powers weather and plants", emoji: "💧" },
  { label: "Fractions", value: "why fractions help us share treats fairly", emoji: "🍰" },
  { label: "Photosynthesis", value: "how plants turn sunlight into snacks", emoji: "🌿" },
  { label: "Gravity", value: "how gravity works on Earth and in space", emoji: "🛰️" },
  { label: "Animal Adaptations", value: "how animals adapt to survive extreme places", emoji: "🦊" },
  { label: "Electricity", value: "how electricity flows through circuits", emoji: "⚡" },
  { label: "History Heroes", value: "why Harriet Tubman was so brave", emoji: "🛤️" },
  { label: "Geometry", value: "how shapes build roller coasters", emoji: "🎢" },
];

const mashupIdeas = [
  { label: "Fortnite build battles", value: "Fortnite build battles", emoji: "🎮" },
  { label: "Soccer championship", value: "a high-stakes soccer championship", emoji: "⚽" },
  { label: "Roblox obby", value: "a tricky Roblox obby", emoji: "🧱" },
  { label: "Dance crew", value: "a dance crew competition", emoji: "🪩" },
  { label: "Minecraft builders", value: "a Minecraft mega build", emoji: "⛏️" },
  { label: "Skate park", value: "a futuristic skate park", emoji: "🛹" },
  { label: "Comic universe", value: "a comic-book multiverse", emoji: "📚" },
  { label: "Baking show", value: "a baking show finale", emoji: "🧁" },
];

const heroIdeas = [
  {
    id: "coach",
    label: "Coach Nova",
    emoji: "🚀",
    tagline: "Hype coach who pauses time for teachable moments",
    value: "Coach Nova, an intergalactic sports coach who freezes time for teachable moments",
  },
  {
    id: "detective",
    label: "Detective Dot",
    emoji: "🕵️‍♀️",
    tagline: "Kid detective with a backpack lab",
    value: "Detective Dot, a clever kid detective with a backpack science lab",
  },
  {
    id: "dj",
    label: "DJ Ripple",
    emoji: "🎧",
    tagline: "AI DJ who remixes facts into beats",
    value: "DJ Ripple, a friendly AI DJ who remixes science facts into music",
  },
  {
    id: "explorer",
    label: "Captain Comet",
    emoji: "🐉",
    tagline: "Eco-explorer who talks to animals",
    value: "Captain Comet, an eco-explorer who teams up with talking animals",
  },
];

const toneOptions = [
  {
    id: "epic",
    label: "Epic Quest",
    emoji: "🌠",
    description: "Bold, cinematic, full of cliffhangers",
    artDirection: "cinematic concept art with glowing light trails and sweeping motion",
  },
  {
    id: "silly",
    label: "Silly & Bright",
    emoji: "😄",
    description: "Laugh-out-loud moments and sound effects",
    artDirection: "playful cartoon illustration with bold outlines and candy colors",
  },
  {
    id: "cozy",
    label: "Cozy Bedtime",
    emoji: "🌙",
    description: "Soft, calm, and heartwarming",
    artDirection: "gentle watercolor storybook art with warm, glowing lighting",
  },
  {
    id: "mystery",
    label: "Detective Mystery",
    emoji: "🕵️",
    description: "Clues, suspense, and smart reveals",
    artDirection: "stylized noir comic panels with bold shadows and neon accents",
  },
];

const goalOptions = [
  {
    id: "teach",
    label: "Teach it clearly",
    blurb: "Step-by-step explanations kids can repeat",
    prompt:
      "Explain the academic concept clearly with vivid metaphors and end with a kid-friendly recap list.",
  },
  {
    id: "wow",
    label: "Blow my mind",
    blurb: "Surprising facts + big reveal moment",
    prompt: "Drop at least one surprising fact and a cinematic twist that ties back to the core lesson.",
  },
  {
    id: "remember",
    label: "Help me remember",
    blurb: "Mini check-ins + mnemonic hooks",
    prompt: "Add gentle comprehension questions and a memorable mnemonic device the child can reuse.",
  },
];

const DEFAULT_VOICE_DESCRIPTION = "TyB's calm explorer storyteller voice (kid-friendly David Attenborough energy).";

const storyShapes = [
  {
    id: "quest",
    label: "Quest Path",
    beats: ["Mission", "Journey", "Challenge", "Victory"],
    prompt:
      "Frame the lesson as a quest with a clear mission, a tricky obstacle, a clever solution, and a triumphant takeaway.",
  },
  {
    id: "mystery",
    label: "Mystery Reveal",
    beats: ["Clue", "Investigation", "Twist", "Mic Drop"],
    prompt: "Tell it like a mystery that slowly reveals the science behind the concept.",
  },
  {
    id: "showtime",
    label: "Showtime Remix",
    beats: ["Opening number", "Backstage plan", "Epic performance", "Encore lesson"],
    prompt: "Structure it like a live performance where each act teaches a new beat.",
  },
];

const lengthOptions = [
  { id: "mini", label: "Quick (3 scenes)", value: "short", sections: 3, helper: "Perfect for speedy sparks" },
  {
    id: "classic",
    label: "Classic (4 scenes)",
    value: "medium",
    sections: 4,
    helper: "Balanced depth + excitement",
  },
  { id: "epic", label: "Epic (5 scenes)", value: "long", sections: 5, helper: "Extra world-building moments" },
];

const stagePrompts = {
  topic: "What are you curious about today? Pick anything from school or life.",
  mashup: "Love it! What should we mix it with—games, hobbies, sports, anything fun?",
  hero: "Who should guide this adventure? A coach, DJ, detective, you decide!",
  tone: "How should it feel? Epic, silly, cozy, or mysterious?",
  goal: "How should this story help you? Explain it? Wow you? Help it stick?",
  ready: "Boom! Hit “Create my story” and I'll handle the animation, art, and narration.",
};

const communitySpotlight = [
  {
    title: "Solar System Soccer Cup",
    tags: ["Space", "Coach Nova", "Soccer"],
    description: "Coach Nova explained gravity by turning each planet into a goalie with special moves.",
  },
  {
    title: "The Fraction Bake-Off",
    tags: ["Fractions", "Baking Show"],
    description: "DJ Ripple remixed cupcake math so teammates could taste-test equal halves.",
  },
  {
    title: "Mystery of the Melting Snowman",
    tags: ["States of Matter", "Detective Dot"],
    description: "Detective Dot cracked the case with magnifying-glass heat maps and kid clues.",
  },
];

const createDefaultFlowState = () => ({
  topic: "",
  mashup: "",
  hero: heroIdeas[0],
  tone: toneOptions[0],
  goal: goalOptions[0],
  storyShape: storyShapes[0],
  extraDetails: "",
  readingLength: lengthOptions[1].value,
});

const pickRandom = (list) => list[Math.floor(Math.random() * list.length)];

const buildStoryPrompt = (flowState, kidName) => {
  const lengthSetting = lengthOptions.find((option) => option.value === flowState.readingLength) || lengthOptions[1];
  const hero = typeof flowState.hero === "string" ? flowState.hero : flowState.hero?.value;
  const beats = flowState.storyShape?.beats?.join(" ➜ ");
  const toneLine = flowState.tone
    ? `${flowState.tone.label} (${flowState.tone.description}) with art that feels like ${flowState.tone.artDirection}`
    : "";
  const artDirectionLine = flowState.tone
    ? `Illustrations should feel like ${flowState.tone.artDirection}.`
    : "Illustrations should feel cinematic, colorful, and kid-friendly.";
  const lines = [
    `Create an original children's story that helps ${kidName || "the learner"} understand: ${flowState.topic}.`,
    `Blend the explanation with this obsession so it feels personal: ${flowState.mashup}.`,
    `Speak directly to the child by name at least once and let them feel like the hero.`,
    `Narrate through the lens of ${hero}, but never introduce yourself as the narrator (no "I'm DJ Ripple").`,
    `Mood & pacing: ${toneLine}.`,
    `Story goal: ${flowState.goal?.label}. ${flowState.goal?.prompt}`,
    `Structure the narrative with these beats: ${beats}. ${flowState.storyShape?.prompt}`,
    flowState.extraDetails?.trim()
      ? `Non‑negotiable detail(s) requested by the learner: ${flowState.extraDetails.trim()}.`
      : null,
    `Write ${lengthSetting.sections} sections (4-6 sentences each). Do NOT number or bullet the section titles—use clean Title Case headings only.`,
    `Keep language vivid, add playful sound cues when helpful, and end every section with a sentence that reinforces or questions the lesson.`,
    `${artDirectionLine} After each section, also provide a single illustration prompt describing the exact scene with camera angle + lighting notes.`,
    'Do not use markdown, asterisks, or emoji in the story text.',
    'Respond in JSON with keys "story" (array of sections) and "image_prompts" (array). Keep it age-appropriate, energetic, and interactive.',
  ];

  return lines.filter(Boolean).join("\n");
};

const StoryTellingChat = ({
  formToken,
  onSaveChat = () => {},
  chatId,
  chatStory,
  kidName,
  canGenerateAudio = true,
  canGenerateImages = true,
  canTogglePublic = true,
  readOnlyStory = false,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [storyParts, setStoryParts] = useState(chatStory?.storyParts || []);
  const [chatStoryId, setChatStoryId] = useState(chatStory?.id || null);
  const [userPrompt, setUserPrompt] = useState(chatStory?.prompt || "");
  const [audioStatus, setAudioStatus] = useState({});
  const [flowState, setFlowState] = useState(createDefaultFlowState);
  const [showBuilder, setShowBuilder] = useState(!(chatStory?.storyParts?.length));
  const [manualPrompt, setManualPrompt] = useState("");
  const [useManualPrompt, setUseManualPrompt] = useState(false);
  const [shareFeedback, setShareFeedback] = useState("");
  const [isPublicStory, setIsPublicStory] = useState(chatStory?.isPublic || false);
  const [publicSaving, setPublicSaving] = useState(false);
  const [publicError, setPublicError] = useState("");
  const friendlyName = kidName || "friend";
  const canEditStory = canTogglePublic || canGenerateAudio || canGenerateImages;

  const completionPieces = [
    flowState.topic.trim(),
    flowState.mashup.trim(),
    flowState.hero,
    flowState.tone,
    flowState.goal,
  ];
  const completion = Math.round((completionPieces.filter(Boolean).length / completionPieces.length) * 100);
  const builderReady = flowState.topic.trim().length > 0 && flowState.mashup.trim().length > 0 && !!flowState.hero;
  const manualReady = useManualPrompt && manualPrompt.trim().length > 10;
  const canSubmit = manualReady || builderReady;

  const stageOrder = ["topic", "mashup", "hero", "tone", "goal"];
  const nextStage = stageOrder.find((key) => {
    if (key === "topic") return !flowState.topic.trim();
    if (key === "mashup") return !flowState.mashup.trim();
    if (key === "hero") return !flowState.hero;
    if (key === "tone") return !flowState.tone;
    if (key === "goal") return !flowState.goal;
    return false;
  }) || "ready";
  const guideMessage = stagePrompts[nextStage];

  const updateFlow = (key, value) => {
    setFlowState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const surpriseMe = () => {
    setFlowState((prev) => ({
      ...prev,
      topic: pickRandom(topicIdeas).value,
      mashup: pickRandom(mashupIdeas).value,
      hero: pickRandom(heroIdeas),
      tone: pickRandom(toneOptions),
      goal: pickRandom(goalOptions),
      storyShape: pickRandom(storyShapes),
      readingLength: pickRandom(lengthOptions).value,
    }));
  };

  const handleSendStoryPrompt = async (prompt) => {
    if (!prompt) return;
    setIsSubmitting(true);
    setUserPrompt(prompt);
    setShareFeedback("");
    try {
      const queryParams = new URLSearchParams({ prompt }).toString();
      const response = await axios.post(
        `/api/v1/chats/${chatId}/tell_story?${queryParams}`,
        {},
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
          },
        }
      );
      setChatStoryId(response.data.id);
      const storySegments = response.data.sections;
      setStoryParts(storySegments);
      onSaveChat();
      setIsPublicStory(false);
      setPublicError("");
      setShowBuilder(false);
      setUseManualPrompt(false);
    } catch (error) {
      console.error("Error generating story:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGenerateAudio = async (messageId) => {
    setAudioStatus((prev) => ({ ...prev, [messageId]: "generating" }));
    try {
      const response = await axios.post(
        `/api/v1/chat_story_sections/${messageId}/generate_audio`,
        {},
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
          },
        }
      );
      setAudioStatus((prev) => ({ ...prev, [messageId]: "ready" }));
      const updatedStoryParts = storyParts.map((part) =>
        part.id === messageId ? { ...part, audioUrl: response.data.audio_url } : part
      );
      setStoryParts(updatedStoryParts);
    } catch (error) {
      console.error("Error generating audio:", error);
      setAudioStatus((prev) => ({ ...prev, [messageId]: "error" }));
    }
  };

  const handleCreateStory = () => {
    if (!canSubmit) return;
    const prompt = manualReady ? manualPrompt.trim() : buildStoryPrompt(flowState, friendlyName);
    handleSendStoryPrompt(prompt);
  };

  const handleShareStory = async () => {
    if (!storyParts.length) return;
    const shareTopic = userPrompt || flowState.topic || "this awesome topic";
    const shareMessage = `I just made a TyB story about: ${shareTopic}!`;
    try {
      if (typeof navigator !== "undefined" && navigator.share) {
        await navigator.share({
          title: "My TyB Story",
          text: shareMessage,
          url: window.location.href,
        });
        setShareFeedback("Shared! Let's inspire another kid ✨");
      } else if (typeof navigator !== "undefined" && navigator.clipboard) {
        await navigator.clipboard.writeText(`${shareMessage}\n${window.location.href}`);
        setShareFeedback("Link copied! Send it to a friend 🤝");
      } else {
        setShareFeedback("Sharing is coming soon!");
      }
    } catch (error) {
      setShareFeedback("No worries—share it whenever you're ready!");
    }
  };

  const resetForNewStory = () => {
    setShowBuilder(true);
    setStoryParts([]);
    setChatStoryId(null);
    setUserPrompt("");
    setAudioStatus({});
    setShareFeedback("");
    setFlowState(createDefaultFlowState());
    setIsPublicStory(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleTogglePublic = async (nextValue = true) => {
    if (!canTogglePublic || !chatStoryId || publicSaving || nextValue === isPublicStory) return;
    setPublicSaving(true);
    setPublicError("");

    try {
      const response = await axios.patch(
        `/api/v1/chat_stories/${chatStoryId}`,
        {
          chat_story: { public: nextValue },
        },
        {
          headers: {
            Authorization: `Bearer ${formToken}`,
            "Content-Type": "application/json",
            Accept: "application/json",
          },
        }
      );
      setIsPublicStory(response.data.public);
    } catch (error) {
      console.error("Error updating story visibility:", error);
      setPublicError("Couldn't update visibility. Please try again.");
    } finally {
      setPublicSaving(false);
    }
  };

  useEffect(() => {
    if (chatStory) {
      setStoryParts(
        chatStory.storyParts.map((part) => ({
          ...part,
          audioUrl: part.audioUrl || null,
        }))
      );
      setChatStoryId(chatStory.id);
      setUserPrompt(chatStory.prompt);
      setShowBuilder(!chatStory.storyParts.length);
      setIsPublicStory(chatStory.isPublic || false);
      const initialAudioStatus = {};
      chatStory.storyParts.forEach((part) => {
        initialAudioStatus[part.id] = part.audioUrl ? "ready" : "not_generated";
      });
      setAudioStatus(initialAudioStatus);
    }
  }, [chatStory]);

  const summaryLines = [
    flowState.topic && `Learning: ${flowState.topic}`,
    flowState.mashup && `Remix with: ${flowState.mashup}`,
    flowState.hero?.label && `Hero: ${flowState.hero.label}`,
    flowState.tone?.label && `Mood: ${flowState.tone.label}`,
    flowState.goal?.label && `Goal: ${flowState.goal.label}`,
  ].filter(Boolean);

  const lengthSetting = lengthOptions.find((option) => option.value === flowState.readingLength) || lengthOptions[1];

  const showHeroCard = showBuilder && !storyParts.length;

  return (
    <div className="w-full max-w-6xl mx-auto space-y-10">
      {isSubmitting && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-30">
          <div className="bg-white rounded-2xl px-8 py-6 shadow-2xl text-center space-y-3 max-w-sm">
            <p className="text-4xl animate-pulse">✨</p>
            <p className="text-lg font-semibold text-slate-900">Mixing story magic...</p>
            <p className="text-sm text-slate-500">
              Crafting narration, art prompts, and audio so you can just enjoy the show.
            </p>
          </div>
        </div>
      )}

      {showHeroCard && (
        <section className="bg-gradient-to-br from-[#5D5FEF] via-[#7F8CFF] to-[#B977FF] text-white rounded-3xl p-8 shadow-xl space-y-6">
          <div className="flex items-start justify-between gap-6 flex-wrap">
            <div>
              <p className="uppercase tracking-[0.5rem] text-sm font-semibold opacity-80">Story Mode</p>
              <h1 className="text-3xl sm:text-4xl font-black leading-tight mt-3">
                Hey {friendlyName}! Let’s cook up a story kids will want to share.
              </h1>
              <p className="text-white/90 mt-3 max-w-2xl">
                Pick a question, remix it with your favorite fandom, and TyB will write it, paint it, read it aloud, and
                prep it for other curious kids to enjoy.
              </p>
            </div>
            <button
              onClick={surpriseMe}
              className="bg-white/20 hover:bg-white/30 text-white rounded-full px-6 py-2 font-semibold transition"
            >
              Surprise me ✨
            </button>
          </div>
          <div className="grid sm:grid-cols-3 gap-4">
            {[
              { title: "Story + Lesson", body: "Explains any topic in story beats you can retell." },
              { title: "Art Studio", body: "Custom illustration prompts for every scene." },
              { title: "Read Aloud", body: "Tap once to hear it in a friendly kid voice." },
            ].map((card) => (
              <div key={card.title} className="bg-white/10 rounded-2xl p-4">
                <p className="text-sm uppercase tracking-wide opacity-80">{card.title}</p>
                <p className="text-base font-medium">{card.body}</p>
              </div>
            ))}
          </div>
        </section>
      )}

      {showBuilder && (
        <section className="space-y-6">
          <div className="bg-white rounded-3xl shadow-xl p-6 flex gap-4 items-start border border-slate-100">
            <div className="text-4xl">🤖</div>
            <div>
              <p className="text-sm uppercase tracking-widest text-indigo-500">Ty the Story DJ</p>
              <p className="text-lg font-semibold text-slate-900 mt-1">{guideMessage}</p>
              <p className="text-sm text-slate-500 mt-2">
                I'll keep asking little questions until I have everything I need to go full storyteller.
              </p>
            </div>
          </div>

          <div className="grid lg:grid-cols-[2fr_1fr] gap-6">
            <div className="space-y-8">
              <div className="bg-white rounded-3xl shadow p-6 space-y-4 border border-slate-100">
                <div>
                  <p className="text-xs uppercase tracking-[0.3rem] text-slate-400">Step 1</p>
                  <h2 className="text-2xl font-bold text-slate-900">What should we explain?</h2>
                  <p className="text-sm text-slate-500">
                    Think of a homework problem, class topic, or random curiosity.
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {topicIdeas.map((idea) => (
                    <button
                      key={idea.label}
                      className={`px-4 py-2 rounded-full border transition text-sm ${
                        flowState.topic === idea.value
                          ? "bg-indigo-50 border-indigo-400 text-indigo-600"
                          : "border-slate-200 text-slate-500 hover:border-indigo-200"
                      }`}
                      onClick={() => updateFlow("topic", idea.value)}
                    >
                      <span className="mr-2">{idea.emoji}</span>
                      {idea.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={flowState.topic}
                  onChange={(e) => updateFlow("topic", e.target.value)}
                  placeholder="e.g., Why does the moon change shape?"
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                />
              </div>

              <div className="bg-white rounded-3xl shadow p-6 space-y-4 border border-slate-100">
                <div>
                  <p className="text-xs uppercase tracking-[0.3rem] text-slate-400">Step 2</p>
                  <h2 className="text-2xl font-bold text-slate-900">Remix it with something you love</h2>
                  <p className="text-sm text-slate-500">This keeps the story insanely personal.</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  {mashupIdeas.map((idea) => (
                    <button
                      key={idea.label}
                      className={`px-4 py-2 rounded-full border transition text-sm ${
                        flowState.mashup === idea.value
                          ? "bg-pink-50 border-pink-400 text-pink-600"
                          : "border-slate-200 text-slate-500 hover:border-pink-200"
                      }`}
                      onClick={() => updateFlow("mashup", idea.value)}
                    >
                      <span className="mr-2">{idea.emoji}</span>
                      {idea.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={flowState.mashup}
                  onChange={(e) => updateFlow("mashup", e.target.value)}
                  placeholder="Maybe tie it to my soccer league or the comic I'm writing..."
                  className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-pink-300"
                />
              </div>

              <div className="bg-white rounded-3xl shadow p-6 space-y-6 border border-slate-100">
                <div>
                  <p className="text-xs uppercase tracking-[0.3rem] text-slate-400">Step 3</p>
                  <h2 className="text-2xl font-bold text-slate-900">Choose the vibe</h2>
                  <p className="text-sm text-slate-500">Hero, tone, and story shape—it all sets the mood.</p>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400 uppercase">Hero / Guide</p>
                  <div className="grid md:grid-cols-2 gap-4">
                    {heroIdeas.map((hero) => (
                      <button
                        key={hero.id}
                        onClick={() => updateFlow("hero", hero)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          flowState.hero?.id === hero.id
                            ? "border-indigo-400 bg-indigo-50/70"
                            : "border-slate-200 hover:border-indigo-200"
                        }`}
                      >
                        <div className="flex items-center gap-3">
                          <span className="text-3xl">{hero.emoji}</span>
                          <div>
                            <p className="font-semibold text-slate-900">{hero.label}</p>
                            <p className="text-sm text-slate-500">{hero.tagline}</p>
                          </div>
                        </div>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400 uppercase">Tone</p>
                  <div className="grid sm:grid-cols-2 gap-3">
                    {toneOptions.map((tone) => (
                      <button
                        key={tone.id}
                        onClick={() => updateFlow("tone", tone)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          flowState.tone?.id === tone.id
                            ? "border-purple-400 bg-purple-50/70"
                            : "border-slate-200 hover:border-purple-200"
                        }`}
                      >
                        <p className="font-semibold text-slate-900">
                          <span className="mr-2">{tone.emoji}</span>
                          {tone.label}
                        </p>
                        <p className="text-sm text-slate-500">{tone.description}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400 uppercase">Story goal</p>
                  {goalOptions.map((goal) => (
                    <button
                      key={goal.id}
                      onClick={() => updateFlow("goal", goal)}
                      className={`w-full rounded-2xl border px-4 py-3 text-left text-sm transition ${
                        flowState.goal?.id === goal.id
                          ? "border-emerald-400 bg-emerald-50/80"
                          : "border-slate-200 hover:border-emerald-200"
                      }`}
                    >
                      <p className="font-semibold text-slate-900">{goal.label}</p>
                      <p className="text-slate-500">{goal.blurb}</p>
                    </button>
                  ))}
                </div>

                <div className="rounded-2xl border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-600">
                  🎙️ Narration voice is set to {DEFAULT_VOICE_DESCRIPTION}. More voices coming soon!
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400 uppercase">Story shape</p>
                  <div className="grid md:grid-cols-3 gap-3">
                    {storyShapes.map((shape) => (
                      <button
                        key={shape.id}
                        onClick={() => updateFlow("storyShape", shape)}
                        className={`rounded-2xl border px-4 py-3 text-left transition ${
                          flowState.storyShape?.id === shape.id
                            ? "border-amber-400 bg-amber-50/80"
                            : "border-slate-200 hover:border-amber-200"
                        }`}
                      >
                        <p className="font-semibold text-slate-900">{shape.label}</p>
                        <p className="text-xs text-slate-500">{shape.beats.join(" ➜ ")}</p>
                      </button>
                    ))}
                  </div>
                </div>

                <div className="space-y-2">
                  <p className="text-xs text-slate-400 uppercase">Length</p>
                  <div className="flex flex-wrap gap-2">
                    {lengthOptions.map((option) => (
                      <button
                        key={option.id}
                        onClick={() => updateFlow("readingLength", option.value)}
                        className={`rounded-full px-4 py-2 text-sm border transition ${
                          flowState.readingLength === option.value
                            ? "border-slate-900 bg-slate-900 text-white"
                            : "border-slate-200 text-slate-600 hover:border-slate-400"
                        }`}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div>
                  <p className="text-xs text-slate-400 uppercase mb-2">Add a must-have moment</p>
                  <textarea
                    value={flowState.extraDetails}
                    onChange={(e) => updateFlow("extraDetails", e.target.value)}
                    rows={3}
                    placeholder="Example: mention my dog Pepper, or include a quiz question at the end."
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-slate-300"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 space-y-4">
                <p className="text-xs uppercase tracking-[0.3rem] text-slate-400">Magic meter</p>
                <div className="flex items-center gap-3">
                  <div className="flex-1 h-2 rounded-full bg-slate-100 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 transition-all"
                      style={{ width: `${Math.max(completion, 8)}%` }}
                    />
                  </div>
                  <span className="text-sm font-semibold text-slate-600">{completion}%</span>
                </div>
                <ul className="space-y-2">
                  {summaryLines.length === 0 && (
                    <li className="text-sm text-slate-400">Fill in any combo of details to see your plan.</li>
                  )}
                  {summaryLines.map((line) => (
                    <li key={line} className="text-sm text-slate-600 flex items-center gap-2">
                      <span className="text-indigo-500">•</span>
                      {line}
                    </li>
                  ))}
                </ul>
                <div className="border rounded-2xl p-3 bg-slate-50 text-sm text-slate-500">
                  {lengthSetting.sections} scenes • {lengthSetting.helper}
                </div>
                <button
                  onClick={handleCreateStory}
                  disabled={!canSubmit || isSubmitting}
                  className={`w-full rounded-2xl py-3 font-semibold text-lg transition ${
                    canSubmit && !isSubmitting
                      ? "bg-indigo-600 hover:bg-indigo-700 text-white"
                      : "bg-slate-200 text-slate-500 cursor-not-allowed"
                  }`}
                >
                  {isSubmitting ? "Mixing your story..." : "Create my story"}
                </button>
                <button
                  onClick={() => setUseManualPrompt((prev) => !prev)}
                  className="text-sm text-indigo-600 hover:text-indigo-700 underline"
                >
                  {useManualPrompt ? "Use guided builder instead" : "Prefer to type your own idea?"}
                </button>
                {useManualPrompt && (
                  <textarea
                    value={manualPrompt}
                    onChange={(e) => setManualPrompt(e.target.value)}
                    rows={5}
                    placeholder="Write any detailed prompt and I'll still generate the art + narration."
                    className="w-full rounded-2xl border border-slate-200 px-4 py-3 focus:outline-none focus:ring-2 focus:ring-indigo-300"
                  />
                )}
              </div>
              <div className="bg-indigo-50/70 border border-indigo-100 rounded-3xl p-5 text-sm text-indigo-900">
                <p className="font-semibold mb-1">Heads up</p>
                <p>
                  Stories usually appear in about 8 seconds. If an image ever times out, hop back into the scene and tap
                  regenerate—I'll re-draw it instantly.
                </p>
              </div>
            </div>
          </div>
        </section>
      )}

      {!showBuilder && storyParts.length > 0 && (
        <section className="space-y-6">
          <div className="bg-white rounded-3xl shadow-xl border border-slate-100 p-6 flex flex-col gap-4">
            <div className="space-y-3">
              <div className="flex items-center gap-3 flex-wrap">
                <div>
                  <p className="text-xs uppercase tracking-[0.3rem] text-slate-400">Story complete</p>
                  <h2 className="text-2xl font-bold text-slate-900">Your custom story is ready to share</h2>
                </div>
                {isPublicStory && (
                  <span className="inline-flex items-center gap-1 text-xs font-semibold text-green-700 bg-green-50 border border-green-100 rounded-full px-3 py-1">
                    <span>🌟</span> Public story
                  </span>
                )}
              </div>
              {canTogglePublic ? (
                isPublicStory ? (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <p className="text-sm text-slate-600 flex-1">
                      Kids can now discover this in the Story Spotlight feed. Want to take it down? Hide it with one tap.
                    </p>
                    <button
                      onClick={() => handleTogglePublic(false)}
                      disabled={publicSaving}
                      className="rounded-2xl border border-slate-200 px-4 py-2 text-sm font-semibold text-slate-600 hover:bg-slate-50 disabled:opacity-60"
                    >
                      {publicSaving ? "Updating…" : "Hide from feed"}
                    </button>
                  </div>
                ) : (
                  <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                    <button
                      onClick={() => handleTogglePublic(true)}
                      disabled={publicSaving}
                      className="rounded-2xl bg-green-500 text-white font-semibold px-4 py-2 hover:bg-green-600 disabled:opacity-60"
                    >
                      {publicSaving ? "Sharing…" : "Share with other kids"}
                    </button>
                    <p className="text-xs text-slate-500">
                      Once shared, it can appear in Kid Story Spotlight and inspire other readers. You can hide it later if
                      you change your mind.
                    </p>
                  </div>
                )
              ) : (
                <p className="text-sm text-slate-500">
                  Shared by another creator — enjoy reading it and pass it along!
                </p>
              )}
              {publicError && <p className="text-xs text-red-500">{publicError}</p>}
            </div>
            <div className="flex gap-2 flex-wrap justify-end">
              {canEditStory && (
                <button
                  onClick={resetForNewStory}
                  className="rounded-2xl border border-indigo-200 text-indigo-600 font-semibold px-4 py-2 hover:bg-indigo-50 transition"
                >
                  Make another
                </button>
              )}
              <button
                onClick={handleShareStory}
                className="rounded-2xl bg-gradient-to-r from-pink-500 to-orange-400 text-white font-semibold px-4 py-2 shadow-lg shadow-pink-500/30"
              >
                Share this magic
              </button>
            </div>
          </div>

          <ol className="space-y-6">
            {storyParts.map((part, index) => (
              <li
                key={part.id || index}
                className="bg-white rounded-3xl shadow-lg border border-slate-100 p-6 grid md:grid-cols-2 gap-6"
              >
                <div className="space-y-4">
                  <div className="flex items-center justify-between text-xs uppercase tracking-[0.3rem] text-slate-400">
                    <span>Scene {index + 1}</span>
                    <span>{flowState.storyShape?.beats?.[index] || "Story beat"}</span>
                  </div>
                  <p className="text-xl font-semibold text-slate-900 leading-relaxed">
                    {cleanStoryText(part.content)}
                  </p>
                  <div className="flex flex-col sm:flex-row gap-3">
                    {part.audioUrl ? (
                      <audio controls src={part.audioUrl} className="w-full rounded-2xl border border-slate-200" />
                    ) : canGenerateAudio ? (
                      <button
                        onClick={() => handleGenerateAudio(part.id)}
                        disabled={audioStatus[part.id] === "generating"}
                        className="w-full rounded-2xl border border-slate-200 px-4 py-3 text-sm font-semibold text-slate-700 hover:border-slate-400 transition"
                      >
                        {audioStatus[part.id] === "generating" ? "Cooking the audio..." : "Read this part to me"}
                      </button>
                    ) : (
                      <p className="text-xs text-slate-400">
                        Audio will appear once the creator records it.
                      </p>
                    )}
                  </div>
                </div>
                <div className="space-y-3">
                  <ImageSection
                    imageId={part.imageId || ""}
                    image={part.image || ""}
                    formToken={formToken}
                    chatId={chatId}
                    chatStoryId={chatStoryId}
                    allowGeneration={canGenerateImages}
                  />
                </div>
              </li>
            ))}
          </ol>

          {shareFeedback && <p className="text-sm text-slate-500">{shareFeedback}</p>}
        </section>
      )}

    </div>
  );
};

export default StoryTellingChat;
